import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type { ActionButton, ViewModeOption } from 'components/modals';
import {
  ActionMenu, type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, type SelectOption,
} from 'components/ui';
import {
  EnterpriseScreenShell, useStandardConfirms, actionButtonFactory, useCan,
} from 'components/enterprise-shell';
import { useReceiptAssignments } from 'hooks/api/useBanking';
import type { ReceiptAssignmentDto, ReceiptAssignmentQueryParams } from 'api/types';
import { assignmentStatusBadge } from '../../modals/modules/banking/ReceiptFormModal';
import { ReceiptAllocationModal, ReturnReasonDialog } from '../../modals/modules/banking/ReceiptAllocationModal';

// ============================================================================
// RECEIPT WORKSHEET SCREEN (ADR-0024 Slice ① Leg 2) — the BU verification
// inbox. PENDING portions are allocated (AGAINST_REF / ADVANCE / ON_ACCOUNT /
// ADJUSTMENT) and posted, or returned to the capturer.
// ============================================================================

type ViewMode = 'list' | 'grid';

interface ReceiptWorksheetScreenProps {
  moduleIcon?: string;
  tabId?: string;
  /** Present for registry parity; the allocation modal has no minimize support (transactional, short-lived). */
  moduleName?: string;
}

const fmtDate = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');
const fmtMoney = (n: number) => (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ' ' (single space) requests ALL statuses: the query builder drops '' entirely
// and an absent parameter re-triggers the backend's PENDING default.
const STATUS_OPTIONS: SelectOption[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'POSTED', label: 'Posted' },
  { value: 'RETURNED', label: 'Returned' },
  { value: ' ', label: 'All Statuses' },
];

const ReceiptWorksheetScreen: React.FC<ReceiptWorksheetScreenProps> = ({ moduleIcon, tabId }) => {
  const can = useCan('receipt-worksheet');
  const { ConfirmDialogPortal } = useStandardConfirms();

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [queryParams, setQueryParams] = useState<ReceiptAssignmentQueryParams>({ page: 1, pageSize: 25, assignmentStatus: 'PENDING' });
  const { data, loading, pagination, refetch } = useReceiptAssignments(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(
      () => setQueryParams(p => ({ ...p, searchTerm: searchQuery || undefined, page: 1 })),
      400,
    );
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [allocationOpen, setAllocationOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ReceiptAssignmentDto | null>(null);

  const handlePageChange = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  // View + Allocate share the allocation modal — it renders read-only for
  // non-PENDING items and hides Post/Return when the user lacks the action.
  const handleOpenAllocation = useCallback((id: string) => {
    const record = data.find(a => a.id === id);
    if (!record) return;
    setSelectedRecord(record); setAllocationOpen(true);
  }, [data]);

  const handleReturn = useCallback((id: string) => {
    const record = data.find(a => a.id === id);
    if (!record) return;
    setSelectedRecord(record); setReturnOpen(true);
  }, [data]);

  const tableColumns: SyutGridColumn<ReceiptAssignmentDto>[] = useMemo(() => [
    {
      id: 'receiptNo', header: 'Receipt No', accessorKey: 'receiptNo', size: 140,
      cell: ({ row }) => (row.original as ReceiptAssignmentDto).receiptNo || '—',
    },
    {
      id: 'receiptDate', header: 'Receipt Date', accessorKey: 'receiptDate', size: 115,
      cell: ({ row }) => fmtDate((row.original as ReceiptAssignmentDto).receiptDate),
    },
    {
      id: 'bank', header: 'Bank', accessorKey: 'bankLedgerName', size: 160,
      cell: ({ row }) => (row.original as ReceiptAssignmentDto).bankLedgerName || '—',
    },
    {
      id: 'payer', header: 'Payer', size: 160,
      cell: ({ row }) => {
        const a = row.original as ReceiptAssignmentDto;
        return a.payerText || a.partyName || '—';
      },
    },
    {
      id: 'hint', header: 'Hint', size: 170,
      cell: ({ row }) => {
        const a = row.original as ReceiptAssignmentDto;
        return a.partyName || a.partyHintText || '—';
      },
    },
    {
      id: 'amount', header: 'Amount', accessorKey: 'fcAmount', size: 130,
      cell: ({ row }) => {
        const a = row.original as ReceiptAssignmentDto;
        return <span style={{ display: 'block', textAlign: 'right' }}>{fmtMoney(a.fcAmount)} {a.currencyCode || ''}</span>;
      },
    },
    {
      id: 'status', header: 'Status', accessorKey: 'assignmentStatus', size: 110,
      cell: ({ row }) => {
        const a = row.original as ReceiptAssignmentDto;
        return (
          <span style={{ display: 'inline-flex', gap: '0.375rem', alignItems: 'center' }}
            title={a.assignmentStatus === 'RETURNED' ? (a.returnReason || undefined) : (a.allocationTransactionNo || undefined)}>
            {assignmentStatusBadge(a.assignmentStatus)}
          </span>
        );
      },
    },
    {
      id: 'voucher', header: 'Voucher No', accessorKey: 'allocationTransactionNo', size: 130,
      cell: ({ row }) => (row.original as ReceiptAssignmentDto).allocationTransactionNo || '—',
    },
  ], []);

  const getRowActions = useCallback((rec: ReceiptAssignmentDto): ActionMenuItem[] => {
    const actions: ActionMenuItem[] = [
      { id: 'view', label: 'View Details', icon: <IconSystem.eye size={18} />, onClick: () => handleOpenAllocation(rec.id) },
    ];
    if (rec.assignmentStatus === 'PENDING') {
      actions.push({ id: 'allocate', label: 'Allocate', icon: <IconSystem.edit size={18} />, onClick: () => handleOpenAllocation(rec.id) });
      if (can('REJECT')) {
        actions.push({ id: 'return', label: 'Return', icon: <IconSystem.arrowLeft size={18} />, onClick: () => handleReturn(rec.id), variant: 'danger' });
      }
    }
    return actions;
  }, [can, handleOpenAllocation, handleReturn]);

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View' },
  ];

  const actionButtons: ActionButton[] = [
    actionButtonFactory.refresh(refetch),
  ];

  const applyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      assignmentStatus: statusFilter,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [statusFilter, fromDate, toDate]);

  const clearFilters = useCallback(() => {
    setStatusFilter('PENDING'); setFromDate(''); setToDate('');
    setQueryParams(prev => ({ page: 1, pageSize: prev.pageSize, assignmentStatus: 'PENDING' }));
  }, []);

  const appliedCount = [statusFilter !== 'PENDING' ? 'x' : '', fromDate, toDate].filter(Boolean).length;

  return (
    <EnterpriseScreenShell
      title="Receipt Verification Worksheet"
      description="BU inbox of receipt portions — allocate to customer bills, advances or adjustments, then post (ADR-0024)"
      icon={moduleIcon && (IconSystem as any)[moduleIcon]
        ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
        : <IconSystem.check size={18} />}
      tabId={tabId}
      actionButtons={actionButtons}
      searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search receipt no / payer / hint / customer...' }}
      viewModeConfig={{ currentMode: viewMode, modes: viewModeOptions, onChange: (m) => setViewMode(m as ViewMode) }}
      filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount }}
      ConfirmDialogPortal={ConfirmDialogPortal}
      trailingContent={
        filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Status" options={STATUS_OPTIONS} value={statusFilter}
                  onChange={(v) => setStatusFilter((v as string) || 'PENDING')}
                  isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>From Date</label>
                <input type="date" className="form-input" value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>To Date</label>
                <input type="date" className="form-input" value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
            </div>
          </div>
        )
      }
    >
      <div className="data-grid-container">
        {viewMode === 'list' && (
          <SyutGrid data={data} columns={tableColumns} enableRowSelection
            enablePagination manualPagination totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1} pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange}
            rowActions={getRowActions}
            emptyMessage={loading ? 'Loading...' : 'The worksheet is empty — nothing waiting for verification.'}
            frozenRightColumns={['actions']} />
        )}
        {viewMode === 'grid' && (
          <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{
              padding: '1rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
              gap: '1rem', flex: 1, overflow: 'auto', alignContent: 'start',
            }}>
              {data.length === 0 ? (
                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                  <IconSystem.check size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                  <p>{loading ? 'Loading...' : 'The worksheet is empty.'}</p>
                </div>
              ) : (
                data.map((a) => (
                  <div key={a.id} style={{
                    background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
                    borderRadius: '0.75rem', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>{a.receiptNo || '—'}</div>
                        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0.25rem 0 0' }}>
                          {fmtMoney(a.fcAmount)} {a.currencyCode || ''}
                        </h3>
                      </div>
                      <ActionMenu items={getRowActions(a)} />
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                      {a.bankLedgerName || '—'} · {fmtDate(a.receiptDate)}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      {a.payerText || a.partyName || a.partyHintText || '—'}
                    </div>
                    <div>{assignmentStatusBadge(a.assignmentStatus)}</div>
                  </div>
                ))
              )}
            </div>
            {data.length > 0 && (
              <div className="pagination-footer">
                <div className="pagination-info">Showing {data.length} of {pagination.totalItems} items</div>
                <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <select value={queryParams.pageSize ?? 25}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem' }}>
                    {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size} / page</option>)}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <ReceiptAllocationModal isOpen={allocationOpen} assignment={selectedRecord}
        canPost={can('APPROVE')} canSaveDraft={can('UPDATE')} canReturn={can('REJECT')}
        onChanged={refetch}
        onClose={() => { setAllocationOpen(false); setSelectedRecord(null); }} />

      <ReturnReasonDialog isOpen={returnOpen} assignment={selectedRecord}
        onReturned={refetch}
        onClose={() => { setReturnOpen(false); setSelectedRecord(null); }} />
    </EnterpriseScreenShell>
  );
};

export default ReceiptWorksheetScreen;
